import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { CoreService } from '../../../services/core.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './login.component.html',
})
export class LoginComponent implements OnInit {
  /*
   * Shared by every LoginComponent instance.
   * This prevents two component instances from processing the same token.
   */
  private static processingTokens = new Set<string>();

  email = '';
  password = '';
  loading = false;
  message = '';
  success = false;

  currentYear = new Date().getFullYear();

  private apiUrl = environment.baseUrl;
  private loginEndpointURL = `${this.apiUrl}/login`;
  private tokenLoginEndpointURL = `${this.apiUrl}/login-with-token`;

  constructor(
    private http: HttpClient,
    private router: Router,
    private coreService: CoreService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    const companyUid =
      this.route.snapshot.queryParamMap.get('company');

    const rawToken =
      this.route.snapshot.queryParamMap.get('token');

    if (companyUid) {
      localStorage.setItem('company_uid', companyUid);
      localStorage.setItem('cloud_company_uid', companyUid);
    }

    if (rawToken) {
      /*
       * Query parameters can occasionally convert "+" into spaces.
       */
      const token = rawToken.replace(/ /g, '+');

      /*
       * Prevent multiple component instances from using the same token.
       */
      if (LoginComponent.processingTokens.has(token)) {
        console.warn('Duplicate token login blocked.');
        return;
      }

      /*
       * Lock before doing anything asynchronous.
       */
      LoginComponent.processingTokens.add(token);

      /*
       * Remove the token from the address bar without causing Angular
       * navigation. This prevents refreshes or component recreation from
       * processing the token again.
       */
      this.removeTokenFromUrl();

      this.loginWithToken(token);
      return;
    }

    if (localStorage.getItem('userInfo')) {
      this.router.navigate(['/posts'], {
        replaceUrl: true,
      });
    }
  }

  private removeTokenFromUrl(): void {
    const url = new URL(window.location.href);

    url.searchParams.delete('token');

    const cleanUrl =
      `${url.pathname}${url.search}${url.hash}`;

    window.history.replaceState(
      window.history.state,
      document.title,
      cleanUrl
    );
  }

  private clearPreviousSession(): void {
    localStorage.removeItem('userInfo');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('cloud_token');
    localStorage.removeItem('company_id');
    localStorage.removeItem('company');
    localStorage.removeItem('latestAlerts');
    localStorage.removeItem('latestPosts');

    Object.keys(localStorage).forEach((key) => {
      if (
        key.startsWith('reports_visible_columns:') ||
        key.startsWith('qb_visible_columns:')
      ) {
        localStorage.removeItem(key);
      }
    });

    sessionStorage.clear();
    this.coreService.user = {};
  }

  private loginWithToken(token: string): void {
    this.loading = true;
    this.message = '';
    this.success = false;

    console.count('loginWithToken POST');
    console.trace('loginWithToken caller');

    console.log('Sending token:', JSON.stringify(token));
    console.log('Token length:', token.length);

    this.http
      .post<any>(this.tokenLoginEndpointURL, { token })
      .subscribe({
        next: (data) => {
          if (!data?.token || !data?.userID) {
            throw new Error(
              'Missing required fields from server.'
            );
          }

          /*
           * Only destroy the previous session after the new token
           * has been accepted.
           */
          this.clearPreviousSession();

          localStorage.setItem(
            'userInfo',
            JSON.stringify(data)
          );

          localStorage.setItem(
            'auth_token',
            data.token
          );

          this.coreService.user = data;

          this.success = true;
          this.message =
            'Login successful! Redirecting...';

          this.router.navigate(['/posts'], {
            replaceUrl: true,
          });
        },

        error: (err) => {
          LoginComponent.processingTokens.delete(token);

          console.error(
            'Token login status:',
            err.status
          );

          console.error(
            'Token login response:',
            err.error
          );

          console.error(
            'Token login validation errors:',
            err.error?.errors
          );

          /*
           * A late duplicate request may fail after another request
           * already logged the user in. Do not overwrite that success
           * with an invalid-token error.
           */
          if (localStorage.getItem('userInfo')) {
            this.router.navigate(['/posts'], {
              replaceUrl: true,
            });

            return;
          }

          this.success = false;
          this.message =
            err.error?.message ??
            'Token invalid or expired. Please log in normally.';

          this.loading = false;
        },

        complete: () => {
          LoginComponent.processingTokens.delete(token);
          this.loading = false;
        },
      });
  }

  onSubmit(form?: NgForm): void {
    if (form?.invalid) {
      return;
    }

    this.loading = true;
    this.message = '';
    this.success = false;

    const body = {
      Email: this.email,
      Password: this.password,
    };

    this.http
      .post<any>(this.loginEndpointURL, body)
      .subscribe({
        next: (data) => {
          if (!data?.token || !data?.userID) {
            throw new Error(
              'Missing required fields from server.'
            );
          }

          localStorage.setItem(
            'userInfo',
            JSON.stringify(data)
          );

          localStorage.setItem(
            'auth_token',
            data.token
          );

          this.coreService.user = data;

          this.success = true;
          this.message =
            'Login successful! Redirecting...';

          this.router.navigate(['/posts'], {
            replaceUrl: true,
          });
        },

        error: (err) => {
          console.error(
            'Login request error:',
            err
          );

          this.success = false;
          this.message =
            'Invalid credentials provided';

          this.loading = false;
        },

        complete: () => {
          this.loading = false;
        },
      });
  }
}